import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API } from '../App';
import { Star, Check, ArrowRight, BookOpen } from 'lucide-react';

const Courses = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/public/courses`)
      .then(res => {
        setCourses(res.data);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-electric-cyan"></div>
      </div>
    );
  }

  return (
    <div className="py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-electric-cyan/10 border border-electric-cyan/30 rounded-full px-4 py-2 mb-6">
            <BookOpen className="w-4 h-4 text-electric-cyan" />
            <span className="text-electric-cyan text-sm font-medium">Comprehensive Programs</span>
          </div>
          <h1 className="font-outfit text-4xl md:text-6xl font-bold text-white mb-4" data-testid="courses-title">
            Our Courses
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Expert tutoring for all major international boards. Choose the program that fits your goals.
          </p>
        </motion.div>

        {/* Featured Course */}
        {courses.filter(c => c.is_featured).slice(0, 1).map((course, idx) => (
          <Link 
            key={course.id}
            to={`/courses/${course.id}`}
            className="block mb-12 select-none"
            data-testid={`featured-course-card-${course.id}`}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="glass-card p-8 md:p-12 relative overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg hover:shadow-electric-cyan/10 hover:border-electric-cyan/30"
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-electric-cyan/5 rounded-full blur-3xl"></div>
              <div className="relative z-10 grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="inline-flex items-center gap-1 bg-electric-cyan/20 text-electric-cyan text-sm font-medium px-3 py-1 rounded-full mb-4">
                    <Star className="w-4 h-4" /> Most Popular
                  </div>
                  <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mb-4">{course.name}</h2>
                  <p className="text-slate-400 text-lg mb-6">{course.description}</p>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {course.grade_levels?.map((grade, i) => (
                      <span key={i} className="text-sm bg-surface-navy px-3 py-1 rounded-full text-slate-300">{grade}</span>
                    ))}
                  </div>
                  <div className="flex items-baseline gap-2 mb-6">
                    <span className="text-4xl font-outfit font-bold text-electric-cyan">SGD {course.price}</span>
                    <span className="text-slate-400">/ {course.duration}</span>
                  </div>
                  <span className="btn-primary inline-flex items-center gap-2" data-testid="featured-enroll-btn">
                    View Course <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
                <div className="space-y-3">
                  <h3 className="font-outfit text-xl font-semibold text-white mb-4">What's Included</h3>
                  {course.features?.map((feature, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-electric-cyan/20 flex items-center justify-center flex-shrink-0">
                        <Check className="w-4 h-4 text-electric-cyan" />
                      </div>
                      <span className="text-slate-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </Link>
        ))}

        {/* Course Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.filter(c => !c.is_featured).map((course, idx) => (
            <Link
              key={course.id}
              to={`/courses/${course.id}`}
              className="block select-none"
              data-testid={`course-card-${course.id}`}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + idx * 0.05 }}
                className="glass-card p-6 flex flex-col h-full cursor-pointer transition-all duration-300 hover:shadow-lg hover:shadow-electric-cyan/10 hover:border-electric-cyan/30 hover:-translate-y-1"
              >
                <h3 className="font-outfit text-2xl font-semibold text-white mb-2">{course.name}</h3>
                <p className="text-slate-400 mb-4 flex-grow">{course.description}</p>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  {course.subjects?.map((subject, i) => (
                    <span key={i} className="text-xs bg-electric-cyan/10 text-electric-cyan px-2 py-1 rounded">{subject}</span>
                  ))}
                </div>
                
                <div className="space-y-2 mb-6">
                  {course.features?.slice(0, 3).map((feature, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-slate-400">
                      <Check className="w-4 h-4 text-electric-cyan flex-shrink-0" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
                
                <div className="flex items-center justify-between pt-4 border-t border-white/10 mt-auto">
                  <div>
                    <span className="text-2xl font-outfit font-bold text-electric-cyan">SGD {course.price}</span>
                    <span className="text-slate-400 text-sm block">{course.duration}</span>
                  </div>
                  <span 
                    className="text-electric-cyan bg-electric-cyan/10 px-4 py-2 rounded-full text-sm font-medium inline-flex items-center gap-1"
                  >
                    View <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </motion.div>
            </Link>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <p className="text-slate-400 mb-4">Not sure which course is right for you?</p>
          <Link to="/contact" className="btn-secondary inline-flex items-center gap-2">
            Get Free Consultation <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default Courses;
